
<template>
  <div class="preview-amortisation">
    <v-card rounded="lg" elevation="2" class="chart-body mt-2">
      <!-- Loading -->
      <v-skeleton-loader v-if="state === 'loading'" type="image" class="rounded-lg" />

      <!-- No project prices -->
      <v-alert
        v-else-if="state === 'no-prices'"
        type="info"
        variant="tonal"
        density="comfortable"
      >
        <strong>Energiepreise fehlen.</strong>
        Hinterlegen Sie die Energiepreise in den
        <router-link :to="{ name: 'ProjectDetails' }">Projektdetails</router-link>,
        um die Amortisation zu sehen.
      </v-alert>

      <!-- No investment entered — the motivation CTA -->
      <v-alert
        v-else-if="state === 'no-invest'"
        type="info"
        variant="tonal"
        density="comfortable"
      >
        <strong>Investitionskosten eingeben, um die Amortisation zu sehen.</strong>
        Tragen Sie in den finanziellen Parametern der Maßnahme die
        Anschaffungs- bzw. Demontagekosten ein – daraus wird die statische
        Amortisationszeit berechnet.
      </v-alert>

      <!-- Positive investment but no annual savings -->
      <v-alert
        v-else-if="state === 'no-savings'"
        type="warning"
        variant="tonal"
        density="comfortable"
      >
        Diese Maßnahme amortisiert sich nicht (keine jährliche Netto-Einsparung).
      </v-alert>

      <!-- Amortisation available: KPI tiles + break-even chart -->
      <template v-else>
        <v-row dense class="mb-2">
          <v-col cols="12" sm="4">
            <div class="kpi">
              <div class="kpi-label">Statische Amortisation</div>
              <div class="kpi-value">{{ formatYears(economics.amortisation) }}</div>
            </div>
          </v-col>
          <v-col cols="12" sm="4">
            <div class="kpi">
              <div class="kpi-label">Investition</div>
              <div class="kpi-value">{{ formatEuro(economics.invest) }}</div>
            </div>
          </v-col>
          <v-col cols="12" sm="4">
            <div class="kpi">
              <div class="kpi-label">Jährliche Einsparung</div>
              <div class="kpi-value">{{ formatEuro(economics.annualSavings) }}/a</div>
            </div>
          </v-col>
        </v-row>

        <!--
          450 statt der früheren 300: LineChart trug ein `min-height: 450px`
          in seinem eigenen CSS, das die 300 überstimmte — die Vorschau war
          also immer 450 px hoch, die Angabe hier war wirkungslos. Das
          min-height ist jetzt aus der geteilten Komponente heraus (es hat
          dort die Ausrichtung der VALERI-Karten gekostet), deshalb steht die
          tatsächliche Höhe nun hier. Die Darstellung bleibt damit gleich.
        -->
        <LineChart
          :chart-data="datacollection"
          :options="options"
          :height="450"
          downloadable
          :project-name="projectName"
          :method-name="methodName"
          :key="chartKey"
        />
      </template>
    </v-card>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import LineChart from './LineChart.vue'

const props = defineProps({
  economics: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  projectName: { type: String, default: '' },
  methodName: { type: String, default: '' },
})

const state = computed(() => {
  if (props.loading) return 'loading'
  if (!props.economics) return 'no-prices'
  if (!props.economics.hasInvest) return 'no-invest'
  if (props.economics.amortisation == null) return 'no-savings'
  return 'ok'
})

const lineColor = '#2196F3'
const markerColor = '#2E7D32'

const horizon = computed(() => {
  const a = props.economics?.amortisation ?? 0
  return Math.min(Math.max(Math.ceil(a) + 2, 10), 25)
})

const datacollection = computed(() => {
  if (state.value !== 'ok') return { datasets: [] }
  const invest = props.economics.invest
  const annual = props.economics.annualSavings
  const n = horizon.value
  const line = []
  for (let t = 0; t <= n; t++) {
    line.push({ x: t, y: -invest + t * annual })
  }
  return {
    datasets: [
      {
        label: 'Kumulierter Cashflow',
        data: line,
        borderColor: lineColor,
        backgroundColor: lineColor,
        borderWidth: 2,
        pointRadius: 2,
        tension: 0,
        datalabels: { display: false },
      },
      {
        label: 'Break-even',
        data: [{ x: props.economics.amortisation, y: 0 }],
        borderColor: markerColor,
        backgroundColor: markerColor,
        pointRadius: 6,
        pointHoverRadius: 7,
        showLine: false,
        datalabels: {
          display: true,
          align: 'top',
          anchor: 'end',
          color: markerColor,
          font: { weight: 'bold' },
          formatter: () => `Break-even: ${formatYears(props.economics.amortisation)}`,
        },
      },
    ],
  }
})

const options = computed(() => ({
  plugins: {
    title: {
      display: true,
      text: 'Amortisation (statisch)',
      font: { weight: '600', size: 14 },
      padding: { top: 4, bottom: 8 },
    },
    legend: { display: true, position: 'top' },
    tooltip: {
      callbacks: {
        label(ctx) {
          const year = ctx.parsed.x
          return `Jahr ${year}: ${formatEuro(ctx.parsed.y)}`
        },
      },
    },
  },
  scales: {
    x: {
      type: 'linear',
      min: 0,
      title: { display: true, text: 'Jahre' },
      ticks: { stepSize: 1 },
    },
    y: {
      title: { display: true, text: '€' },
      ticks: {
        callback(v) {
          return Number(v).toLocaleString('de-DE')
        },
      },
    },
  },
}))

const chartKey = computed(() => `${horizon.value}-${props.economics?.amortisation ?? 0}`)

function formatYears(years) {
  if (years == null) return '–'
  return `${Number(years).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Jahre`
}

function formatEuro(value) {
  if (value == null) return '–'
  return `${Number(value).toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`
}
</script>

<style scoped>
/* Die feste, zentrierte Breite der Vorschau — dieselbe Angabe wie
   `.preview-chart` in PreviewChart.vue, mit der diese Karte zusammen in einer
   Spalte steht. Sie stand bis jetzt im CSS von LineChart.vue und galt damit
   für jeden Einbauort der Komponente, auch für die breiten VALERI-Karten.
   Hier gehört sie hin: an den Ort, der sie haben will. */
.preview-amortisation {
  width: 450px;
  max-width: 100%;
  margin-inline: auto;
}

.chart-body {
  box-sizing: border-box;
  padding: 12px;
  min-height: 360px;
}

.kpi {
  text-align: center;
  padding: 8px 4px;
}

.kpi-label {
  font-size: 0.8rem;
  opacity: 0.7;
}

.kpi-value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

:deep(canvas) {
  border-radius: 8px;
}
</style>
